import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { API_ENDPOINTS } from "../../../constants/api_endpoints";
import {
  ISupplier, ISupplierCreatePayload, ISupplierUpdatePayload,
  IPartCategory, IPartCategoryCreatePayload, IPartCategoryUpdatePayload,
  IPart, IPartCreatePayload, IPartUpdatePayload,
  IStockLocation, IStockLocationCreatePayload, IStockLocationUpdatePayload,
  IStockMovementCreatePayload,
} from "../../../types/api/inventory";
import {
  listSuppliers, createSupplier, updateSupplier, deleteSupplier,
  listPartCategories, createPartCategory, updatePartCategory, deletePartCategory,
  listParts, createPart, updatePart, deletePart,
  listStockLocations, createStockLocation, updateStockLocation, deleteStockLocation,
  listStockLevels,
  listStockMovements, createStockMovement,
} from "./callbacks";

// ── Suppliers ──────────────────────────────────────────────────────────────
export const useSupplierList = (initialParams: any) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.SUPPLIER_LIST, { ...params, total: null }],
    queryFn: () => listSuppliers(params).then((r) => r.data),
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: ISupplier) => ({ key: x.id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: { page: 1, limit: 20 }, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useSupplierCreate = (options?: { onSuccess?: (d: ISupplier) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ISupplierCreatePayload) => createSupplier(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.SUPPLIER_LIST] }); options?.onSuccess?.(res.data as ISupplier); },
    onError: options?.onError,
  });
};

export const useSupplierUpdate = (options?: { onSuccess?: (d: ISupplier) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: ISupplierUpdatePayload) => updateSupplier(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.SUPPLIER_LIST] }); options?.onSuccess?.(res.data as ISupplier); },
    onError: options?.onError,
  });
};

export const useSupplierDelete = (options?: { onSuccess?: () => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteSupplier(id).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.SUPPLIER_LIST] }); options?.onSuccess?.(); },
  });
};

// ── Part Categories ────────────────────────────────────────────────────────
export const usePartCategoryList = (params?: any) =>
  useQuery({
    queryKey: [API_ENDPOINTS.PART_CATEGORY_LIST, params],
    queryFn: () => listPartCategories(params || {}).then((r) => r.data),
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

export const usePartCategoryCreate = (options?: { onSuccess?: (d: IPartCategory) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IPartCategoryCreatePayload) => createPartCategory(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PART_CATEGORY_LIST] }); options?.onSuccess?.(res.data as IPartCategory); },
    onError: options?.onError,
  });
};

export const usePartCategoryUpdate = (options?: { onSuccess?: (d: IPartCategory) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IPartCategoryUpdatePayload) => updatePartCategory(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PART_CATEGORY_LIST] }); options?.onSuccess?.(res.data as IPartCategory); },
    onError: options?.onError,
  });
};

export const usePartCategoryDelete = (options?: { onSuccess?: () => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deletePartCategory(id).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PART_CATEGORY_LIST] }); options?.onSuccess?.(); },
  });
};

// ── Parts ──────────────────────────────────────────────────────────────────
export const usePartList = (initialParams: any) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.PART_LIST, { ...params, total: null }],
    queryFn: () => listParts(params).then((r) => r.data),
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: IPart) => ({ key: x.id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: { page: 1, limit: 20 }, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 20000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const usePartCreate = (options?: { onSuccess?: (d: IPart) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IPartCreatePayload) => createPart(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PART_LIST] }); options?.onSuccess?.(res.data as IPart); },
    onError: options?.onError,
  });
};

export const usePartUpdate = (options?: { onSuccess?: (d: IPart) => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IPartUpdatePayload) => updatePart(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PART_LIST] }); options?.onSuccess?.(res.data as IPart); },
    onError: options?.onError,
  });
};

export const usePartDelete = (options?: { onSuccess?: () => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deletePart(id).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.PART_LIST] }); options?.onSuccess?.(); },
  });
};

// ── Stock Locations ────────────────────────────────────────────────────────
export const useStockLocationList = (params?: any) =>
  useQuery({
    queryKey: [API_ENDPOINTS.STOCK_LOCATION_LIST, params],
    queryFn: () => listStockLocations(params || {}).then((r) => r.data),
    staleTime: 30000,
    refetchOnWindowFocus: false,
  });

export const useStockLocationCreate = (options?: { onSuccess?: (d: IStockLocation) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IStockLocationCreatePayload) => createStockLocation(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.STOCK_LOCATION_LIST] }); options?.onSuccess?.(res.data as IStockLocation); },
  });
};

export const useStockLocationUpdate = (options?: { onSuccess?: (d: IStockLocation) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IStockLocationUpdatePayload) => updateStockLocation(data).then((r) => r.data),
    onSuccess: (res) => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.STOCK_LOCATION_LIST] }); options?.onSuccess?.(res.data as IStockLocation); },
  });
};

export const useStockLocationDelete = (options?: { onSuccess?: () => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => deleteStockLocation(id).then((r) => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: [API_ENDPOINTS.STOCK_LOCATION_LIST] }); options?.onSuccess?.(); },
  });
};

// ── Stock Levels ───────────────────────────────────────────────────────────
export const useStockLevelList = (params?: any) =>
  useQuery({
    queryKey: [API_ENDPOINTS.STOCK_LEVEL_LIST, params],
    queryFn: () => listStockLevels(params || {}).then((r) => r.data),
    staleTime: 15000,
    refetchOnWindowFocus: false,
  });

// ── Stock Movements ────────────────────────────────────────────────────────
export const useStockMovementList = (initialParams: any) => {
  const [params, setParams] = useState(initialParams);
  const result = useQuery({
    queryKey: [API_ENDPOINTS.STOCK_MOVEMENT_LIST, { ...params, total: null }],
    queryFn: () => listStockMovements(params).then((r) => r.data),
    select: (res: any) => ({
      ...res,
      data: (res.data || []).map((x: any) => ({ key: x.id, ...x })),
      qData: { ...params, total: res.extra?.total },
    }),
    initialData: { data: [], success: true, message: "", code: 200, qData: { page: 1, limit: 20 }, extra: { page: 1, limit: 20, total: 0 } },
    staleTime: 15000,
    initialDataUpdatedAt: 0,
    refetchOnWindowFocus: false,
  });
  return { ...result, refetchWithQuery: (p: any) => setParams((prev: any) => ({ ...prev, ...p })) };
};

export const useStockMovementCreate = (options?: { onSuccess?: () => void; onError?: (e: any) => void }) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: IStockMovementCreatePayload) => createStockMovement(data).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.STOCK_MOVEMENT_LIST] });
      qc.invalidateQueries({ queryKey: [API_ENDPOINTS.STOCK_LEVEL_LIST] });
      options?.onSuccess?.();
    },
    onError: options?.onError,
  });
};
